import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlined from "@mui/icons-material/CategoryOutlined";
import AttachMoney from "@mui/icons-material/AttachMoney";
import { useEffect, useState } from "react";
import { productCategoriesRepository } from "../../product/product-categories.repository";
import { ProductCategory } from "../../product/product-category.entity";
import { v4 } from "uuid";

type ItemAutoComplete = {
  title?: string;
  id: string;
  inputValue?: string;
};

type AddProductSellProps = {
  addOnlyPrice: () => void;
  /** Preenchimento inteligente a partir do que foi digitado no PDV. */
  initialName?: string;
  initialCode?: string;
};

export const AddProductSell = ({
  addOnlyPrice,
  initialName = "",
  initialCode = "",
}: AddProductSellProps) => {
  const [amount, setAmount] = useState("");
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [categorySelected, setCategorySelected] = useState<string>();

  useEffect(() => {
    (async () => {
      const list = await productCategoriesRepository.getAll();
      setCategories(list);
    })();
  }, []);

  return (
    <Stack component="form" spacing={2.5} sx={{ textAlign: "left", py: 0.5 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.info.main, 0.1),
          border: 1,
          borderColor: (t) => alpha(t.palette.info.main, 0.35),
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
          💡 Dica rápida
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Se não quiser cadastrar agora, use{" "}
          <strong>só o valor</strong> — o item entra na venda sem criar produto no estoque.
        </Typography>
        <Button
          fullWidth
          variant="contained"
          color="info"
          sx={{ mt: 2 }}
          onClick={() => addOnlyPrice()}
        >
          💲 Só valor (sem cadastro)
        </Button>
      </Box>

      <Divider>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          ou cadastre o produto
        </Typography>
      </Divider>

      <Alert severity="error" id="amountError" sx={{ opacity: 0, transition: "opacity 0.2s" }}>
        Preencha nome, preço e categoria para continuar.
      </Alert>

      <TextField
        id="productCode"
        label="Código (SKU ou barras)"
        variant="outlined"
        placeholder="Opcional — preenchido automaticamente se você digitou um código"
        fullWidth
        defaultValue={initialCode}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Inventory2Outlined color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <TextField
        id="name"
        label="Nome do produto"
        variant="outlined"
        placeholder="Ex.: Café expresso 200ml"
        autoFocus={!initialCode}
        fullWidth
        defaultValue={initialName}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Inventory2Outlined color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <FormControl fullWidth>
        <InputLabel htmlFor="amount">Preço de venda</InputLabel>
        <OutlinedInput
          id="amount"
          label="Preço de venda"
          value={amount}
          startAdornment={
            <InputAdornment position="start">
              <AttachMoney color="action" fontSize="small" />
            </InputAdornment>
          }
          onChange={(event) => {
            let value = event.target.value.replace(/[^0-9]/g, "");
            event.target.dataset.amount = String(parseFloat(value) / 100);
            value = new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
            })
              .format(Number(value) / 100)
              .replaceAll("[^0123456789.,]", "");
            setAmount(value);
          }}
        />
      </FormControl>

      <Autocomplete
        value={{
          title: categories?.find((c) => c.id === categorySelected)?.title,
          id: categorySelected ?? "",
        }}
        onChange={(event, newValue) => {
          if (typeof newValue === "string") {
            const existingCategory = categories?.find(
              (category) => category.title === newValue
            );

            if (existingCategory) {
              setCategorySelected(existingCategory.id);
              return;
            }
          }

          if (newValue && typeof newValue === "object" && newValue.id !== "_new") {
            setCategorySelected(newValue.id);
            return;
          }

          const newTitle =
            typeof newValue === "string" ? newValue : newValue?.inputValue;
          if (!newTitle) {
            return;
          }

          const newCategory = new ProductCategory(v4(), newTitle, "");
          productCategoriesRepository.save(newCategory);
          setCategorySelected(newCategory.id);
          setCategories((items) => (items ? [...items, newCategory] : [newCategory]));
        }}
        filterOptions={(options, params) => {
          const filtered = createFilterOptions<ItemAutoComplete>()(options, params);

          const { inputValue } = params;
          const isExisting = options.some((option) => inputValue === option.title);
          if (inputValue !== "" && !isExisting) {
            filtered.push({
              id: "_new",
              title: `Adicionar "${inputValue}"`,
              inputValue,
            });
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        options={
          categories?.map(
            (category) =>
              ({
                title: category.title,
                id: category.id,
              }) as ItemAutoComplete
          ) || []
        }
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          }

          return option.title || "";
        }}
        renderOption={(props, option) => (
          <li {...props}>{option.title}</li>
        )}
        freeSolo
        loading={categories === undefined}
        fullWidth
        openOnFocus
        renderInput={(params) => (
          <TextField
            {...params}
            label="Categoria"
            placeholder="Escolha ou digite uma nova"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <CategoryOutlined color="action" fontSize="small" />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
      />

      <input type="hidden" id="categoryId" value={categorySelected ?? ""} />
    </Stack>
  );
};
